import { addMinutes, format, isValid, parseISO, subMinutes } from 'date-fns'
import type {
    DiscountType,
    PromotionDiscountType,
} from '@storefronts/common-types'

const DATE_LINE = 'EEE, MMM d'
const TIME_LINE = 'h:mmaaa'

type ZoneParts = {
    year: string
    month: string
    day: string
    hour: string
    minute: string
}

export function parseInstant(iso: string): Date | null {
    const date = parseISO(iso)
    return isValid(date) ? date : null
}

export function accountTimezoneNote(timeZone?: string): string {
    if (!timeZone) return 'Times are in your account timezone.'
    const name =
        new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'longGeneric',
        })
            .formatToParts(new Date())
            .find((part) => part.type === 'timeZoneName')?.value ?? timeZone
    return `Times shown in ${name} (${timeZone}).`
}

export function exclusiveEndToInclusive(exclusiveIso: string): Date | null {
    const exclusive = parseInstant(exclusiveIso)
    return exclusive ? subMinutes(exclusive, 1) : null
}

export function inclusiveEndToExclusiveIso(inclusive: Date): string {
    return addMinutes(inclusive, 1).toISOString()
}

export function formatDateLine(iso: string, timeZone?: string): string {
    const date = parseInstant(iso)
    if (!date) return iso
    if (!timeZone) return format(date, DATE_LINE)
    return new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(date)
}

export function formatTimeLine(iso: string, timeZone?: string): string {
    const date = parseInstant(iso)
    if (!date) return ''
    if (!timeZone) return format(date, TIME_LINE)
    return formatTimeFromParts(zoneParts(date, timeZone))
}

export function formatInclusiveEndDate(
    exclusiveIso: string,
    timeZone?: string,
): string {
    const inclusive = exclusiveEndToInclusive(exclusiveIso)
    if (!inclusive) return exclusiveIso
    return formatDateLine(inclusive.toISOString(), timeZone)
}

export function formatInclusiveEndTime(
    exclusiveIso: string,
    timeZone?: string,
): string {
    const inclusive = exclusiveEndToInclusive(exclusiveIso)
    if (!inclusive) return ''
    return formatTimeLine(inclusive.toISOString(), timeZone)
}

export function formatInclusiveEndInstant(
    exclusiveIso: string,
    timeZone?: string,
): string {
    const date = formatInclusiveEndDate(exclusiveIso, timeZone)
    const time = formatInclusiveEndTime(exclusiveIso, timeZone)
    return time ? `${date}, ${time}` : date
}

export function formatWindowLine(
    startsAt: string,
    exclusiveEndsAt: string,
    timeZone?: string,
): string {
    return `${formatDateLine(startsAt, timeZone)} → through ${formatInclusiveEndDate(exclusiveEndsAt, timeZone)}`
}

export function formatDaysUntil(
    fromMs: number,
    toIso: string,
    verb: 'Ends' | 'Starts',
): string {
    const target = parseInstant(toIso)
    if (!target) return ''
    const remainingMs = target.getTime() - fromMs
    if (remainingMs <= 0) return `${verb} now`
    const days = Math.ceil(remainingMs / 86_400_000)
    if (days === 1) return `${verb} in 1 day`
    return `${verb} in ${days} days`
}

function pad(value: number): string {
    return String(value).padStart(2, '0')
}

export function toDateInputValue(iso: string, timeZone?: string): string {
    const date = parseInstant(iso)
    if (!date) return ''
    if (!timeZone) {
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    }
    const parts = zoneParts(date, timeZone)
    return `${parts.year}-${parts.month}-${parts.day}`
}

export function toTimeInputValue(iso: string, timeZone?: string): string {
    const date = parseInstant(iso)
    if (!date) return ''
    if (!timeZone) {
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
    const parts = zoneParts(date, timeZone)
    return `${parts.hour}:${parts.minute}`
}

export function toInclusiveEndDateInput(
    exclusiveIso: string,
    timeZone?: string,
): string {
    const inclusive = exclusiveEndToInclusive(exclusiveIso)
    if (!inclusive) return ''
    return toDateInputValue(inclusive.toISOString(), timeZone)
}

export function toInclusiveEndTimeInput(
    exclusiveIso: string,
    timeZone?: string,
): string {
    const inclusive = exclusiveEndToInclusive(exclusiveIso)
    if (!inclusive) return ''
    return toTimeInputValue(inclusive.toISOString(), timeZone)
}

export function fromDateAndTimeInputs(
    dateValue: string,
    timeValue: string,
    timeZone?: string,
): string | null {
    if (!dateValue || !timeValue) return null
    if (timeZone) return zonedWallClockToUtcIso(dateValue, timeValue, timeZone)
    const date = new Date(`${dateValue}T${timeValue}`)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export function exclusiveEndFromInclusiveInputs(
    dateValue: string,
    timeValue: string,
    timeZone?: string,
): string | null {
    const inclusiveIso = fromDateAndTimeInputs(dateValue, timeValue, timeZone)
    if (!inclusiveIso) return null
    const inclusive = parseInstant(inclusiveIso)
    return inclusive ? inclusiveEndToExclusiveIso(inclusive) : null
}

export function instantsEqual(
    left: string | null,
    right: string | null,
): boolean {
    if (left == null && right == null) return true
    if (left == null || right == null) return false
    const leftDate = parseInstant(left)
    const rightDate = parseInstant(right)
    if (!leftDate || !rightDate) return left === right
    return leftDate.getTime() === rightDate.getTime()
}

export function formatShortDate(iso: string, timeZone?: string): string {
    const date = parseInstant(iso)
    if (!date) return iso
    if (!timeZone) return format(date, 'MMM d')
    return new Intl.DateTimeFormat('en-US', {
        timeZone,
        month: 'short',
        day: 'numeric',
    }).format(date)
}

/** Null when there is nothing to show: no amount, or a banner-only promotion. */
export function formatDiscountAmount(
    discountType: DiscountType | PromotionDiscountType | null,
    discountValue: number | null,
): string | null {
    if (discountType == null || discountValue == null) return null
    if (discountType === 'OTHER') return 'Other discount'
    if (discountValue <= 0) return null
    if (discountType === 'PERCENT') return `${discountValue}% off`
    return `$${(discountValue / 100).toFixed(2)} off`
}

export function formatLookupWindow(
    status: string,
    startsAt: string | null,
    endsAt: string | null,
    timeZone?: string,
): string {
    const start = startsAt ? formatShortDate(startsAt, timeZone) : null
    const end = endsAt ? formatInclusiveEndDate(endsAt, timeZone) : null
    const verb =
        status === 'SCHEDULED'
            ? 'scheduled'
            : status === 'EXPIRED'
              ? 'ended'
              : 'active'
    if (start && end) return `${verb} ${start} – ${end}`
    if (start) return `${verb} from ${start}`
    return verb
}

export function promotionSecondaryLine(entry: {
    discountCode: string | null
    discountType: DiscountType
    discountValue: number
    bannerText: string | null
}): string {
    const parts: string[] = []
    if (entry.discountCode) parts.push(entry.discountCode)
    const amount = formatDiscountAmount(entry.discountType, entry.discountValue)
    if (amount) parts.push(amount)
    if (entry.bannerText) parts.push(`“${entry.bannerText}”`)
    return parts.join(' · ')
}

function zoneParts(date: Date, timeZone: string): ZoneParts {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(date)
    const value = (type: string) =>
        parts.find((part) => part.type === type)?.value ?? ''
    const hour = value('hour') === '24' ? '00' : value('hour')
    return {
        year: value('year'),
        month: value('month'),
        day: value('day'),
        hour,
        minute: value('minute'),
    }
}

function formatTimeFromParts(parts: ZoneParts): string {
    const hour24 = Number(parts.hour)
    const suffix = hour24 >= 12 ? 'pm' : 'am'
    const hour12 = hour24 % 12 || 12
    return `${hour12}:${parts.minute}${suffix}`
}

/** Minutes to add to UTC to get the wall clock in timeZone. */
function zoneOffsetMinutes(timeZone: string, utcMs: number): number {
    const parts = zoneParts(new Date(utcMs), timeZone)
    const asUtc = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
    )
    return Math.round((asUtc - utcMs) / 60_000)
}

function zonedWallClockToUtcIso(
    dateValue: string,
    timeValue: string,
    timeZone: string,
): string | null {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue)
    const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue)
    if (!dateMatch || !timeMatch) return null
    const year = Number(dateMatch[1])
    const month = Number(dateMatch[2])
    const day = Number(dateMatch[3])
    const hour = Number(timeMatch[1])
    const minute = Number(timeMatch[2])
    if (
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31 ||
        hour > 23 ||
        minute > 59
    ) {
        return null
    }
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute)
    const offset = zoneOffsetMinutes(timeZone, utcGuess)
    let utc = utcGuess - offset * 60_000
    const offsetAtResult = zoneOffsetMinutes(timeZone, utc)
    if (offsetAtResult !== offset) {
        utc = utcGuess - offsetAtResult * 60_000
    }
    const date = new Date(utc)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
